import { Card, Tabs } from '@amelio/ui-library';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EmployeeProfileCard } from '../EmployeeProfileCard/EmployeeProfileCard';

import type { User } from '@/types/directory/User';

import { scrollPageToTop } from '@/navigation/ScrollToTop';

const TAB_VALUES = [
    'profil',
    'summary',
    'assessment',
    'growth',
    'objectives',
    'notes',
    'competencies',
] as const;

export type TabValue = (typeof TAB_VALUES)[number];

const TAB_PARAM = 'tab';

/**
 * The open tab lives in the address, `?tab=`, so that coming back to the profile -- Back from an
 * objective opened on it, a reload -- reopens the tab the reader left. It is read from and written
 * to the browser's own address rather than through a router: embedded in the host's colleague page,
 * the profile runs under a MemoryRouter that is gone as soon as the reader leaves, and a tab change
 * is no navigation for the host -- no new history entry, no guard run again, the entry's state kept.
 */
const readTabFromAddress = (): TabValue | undefined => {
    const requested = new URLSearchParams(window.location.search).get(TAB_PARAM);

    return TAB_VALUES.find((tab) => tab === requested);
};

const writeTabToAddress = (tab: TabValue) => {
    const url = new URL(window.location.href);
    url.searchParams.set(TAB_PARAM, tab);
    window.history.replaceState(window.history.state, '', url);
};

interface ProfileTabsProps {
    /**
     * The panel behind the active tab. The second argument opens another tab from inside a panel
     * (the summary's "see all objectives" button); a panel that does not need it simply ignores it.
     */
    children: (activeTab: TabValue, goToTab: (tab: TabValue) => void) => React.ReactNode;
    employee?: User;
    jobTitle?: string;
    /** Narrows the tab row to these tabs, in the row's order; the first one opens. All by default. */
    visibleTabs?: readonly [TabValue, ...TabValue[]];
    /** False while the Objectives feature is off: the objectives tab then has nothing behind it. */
    showsObjectives?: boolean;
    /**
     * Whether the reader may see the employee's notes. Off on one's own profile: notes are never
     * shown to the employee they are about, a manager included.
     */
    canReadNotes: boolean;
    /**
     * The tab the row opens on: the one a screen was opened from, when the profile comes back to it.
     * Without it, the tab named in the address, then the assessment tab.
     */
    initialTab?: TabValue;
}

const tabTestId = (tab: TabValue) => `profile-tab-${tab}`;

export const ProfileTabs = ({
    children,
    employee,
    jobTitle,
    visibleTabs,
    showsObjectives = true,
    canReadNotes,
    initialTab,
}: ProfileTabsProps) => {
    const { t } = useTranslation();
    const [selectedTab, setSelectedTab] = useState<TabValue>(
        () => initialTab ?? readTabFromAddress() ?? 'assessment',
    );
    const rootRef = useRef<HTMLDivElement>(null);

    // Only the tabs that have a screen behind them: a tab that opens on "coming soon" is a dead
    // control.
    const allTabs: { value: TabValue; label: string }[] = [
        { value: 'assessment', label: t('performanceProfile.tabs.assessment') },
        { value: 'growth', label: t('performanceProfile.tabs.growth') },
        ...(showsObjectives
            ? [{ value: 'objectives' as const, label: t('performanceProfile.tabs.objectives') }]
            : []),
        ...(canReadNotes
            ? [{ value: 'notes' as const, label: t('performanceProfile.tabs.notesAndFeedback') }]
            : []),
        { value: 'competencies', label: t('performanceProfile.tabs.competencies') },
    ];
    // The competencies tab only exists on its own: the assessment tab already shows them to
    // whoever reads the full profile.
    const tabs = visibleTabs
        ? allTabs.filter((tab) => visibleTabs.includes(tab.value))
        : allTabs.filter((tab) => tab.value !== 'competencies');
    // A tab can leave the list while it is open (the reader turns out to be the employee), and a
    // narrowed row may not hold the default one: the panel then falls back to the first tab.
    const activeTab = tabs.find((tab) => tab.value === selectedTab) ?? tabs[0];

    // A tab is a screen of its own: it opens at the top of the page, like any other screen, rather
    // than wherever the previous tab was left scrolled.
    const selectTab = (tab: TabValue) => {
        setSelectedTab(tab);
        writeTabToAddress(tab);
        scrollPageToTop();
    };

    /**
     * Opens a tab from anywhere, keyboard included: the tab is selected *and* focused, so a reader
     * who followed a link from inside a panel lands on the tab that names where they now are,
     * instead of losing the focus along with the panel that held the link. The tab buttons belong
     * to the library, which exposes no handle on them, so the one to focus is read from the row by
     * the role it guarantees, in the order it was handed — from this component's own root, since
     * the module renders inside a shadow root where a document-wide query finds nothing.
     */
    const goToTab = (tab: TabValue) => {
        selectTab(tab);
        const row = rootRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]');
        row?.[tabs.findIndex((candidate) => candidate.value === tab)]?.focus();
    };

    return (
        <div
            ref={rootRef}
            className={'ds-gap-lg flex flex-col'}
        >
            {employee && (
                <EmployeeProfileCard
                    employee={employee}
                    jobTitle={jobTitle ?? ''}
                />
            )}
            <Card
                padding={'none'}
                className={'ds-mb-8xl ds-px-2xl ds-py-xl !rounded-[12px] backdrop-blur-[30px]'}
            >
                <Tabs
                    variant={'pills'}
                    items={tabs.map((tab) => ({
                        key: tab.value,
                        label: tab.label,
                        dataTestId: tabTestId(tab.value),
                    }))}
                    activeKey={activeTab.value}
                    onChange={(key) => selectTab(key as TabValue)}
                    ariaLabel={t('performanceProfile.tabsAriaLabel')}
                />
            </Card>
            <div
                role={'tabpanel'}
                aria-label={activeTab.label}
            >
                {children(activeTab.value, goToTab)}
            </div>
        </div>
    );
};
